import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "../../content/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled || open ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(27,47,91,0.08)]" : "bg-white/70 backdrop-blur-md"}`}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 bg-white px-4 py-2 rounded-lg">Zum Inhalt springen</a>
      <div className="container-x flex h-[72px] items-center justify-between gap-6">
        <Logo />
        <nav aria-label="Hauptnavigation" className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} className="link-underline text-[0.92rem] font-medium text-slate-700 hover:text-[color:var(--navy)] py-1">
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/personal-anfragen" data-testid="header-cta-inquiry" className="hidden md:inline-flex items-center gap-2 rounded-full bg-[color:var(--navy)] px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-[color:var(--navy-deep)] active:scale-[0.97]">
            Personal anfragen <ArrowUpRight size={16} />
          </Link>
          <button
            type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="mobile-nav"
            aria-label={open ? "Menü schließen" : "Menü öffnen"} data-testid="mobile-menu-toggle"
            className="xl:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-[color:var(--navy)]"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-nav" aria-label="Mobile Navigation"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="xl:hidden h-[calc(100dvh-72px)] overflow-y-auto bg-white border-t border-slate-100"
          >
            <ul className="container-x py-6 flex flex-col">
              {NAV.map((n, i) => (
                <motion.li key={n.to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
                  <NavLink to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`} className="flex items-center justify-between border-b border-slate-100 py-4 text-2xl font-semibold text-[color:var(--navy)]">
                    {n.label} <ArrowUpRight size={20} className="text-[color:var(--teal)]" />
                  </NavLink>
                </motion.li>
              ))}
            </ul>
            <div className="container-x grid gap-3 pb-10">
              <Link to="/personal-anfragen" data-testid="mobile-cta-inquiry" className="rounded-full bg-[color:var(--navy)] py-4 text-center font-semibold text-white">Personal anfragen</Link>
              <Link to="/bewerber-registrierung" data-testid="mobile-cta-register" className="rounded-full border border-[color:var(--navy)] py-4 text-center font-semibold text-[color:var(--navy)]">Als Bewerber registrieren</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
